import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { mapitShipApi } from "src/_api/mapitShipApi";
import { apiSlice } from "src/_api/apiSlice";
import { runWithConcurrency } from "src/_utils";
import {
  BULK_CONCURRENCY,
  BULK_CONTROL,
  BULK_PHASE,
  BULK_SETTLED_STATUSES,
  BULK_STATUS,
  needsCarrier,
} from "./constants";

// How often per-order progress is published to React. A 100-order run produces
// ~400 status changes; committing each one separately re-renders the run table
// (and MUI's DataGrid) hundreds of times and makes the dialog crawl.
const FLUSH_INTERVAL_MS = 200;

/**
 * Matches the carrier the user picked from the generic price-calculator catalog
 * against the carriers this specific order is actually eligible for.
 *
 * Matched on `deliveryOptionId` + `integrationType` only. `deliveryOptionName`
 * and `serviceType` are free-text display values from the server and are not a
 * safe key.
 */
const findMatchingCompany = (deliveryCompany, picked) =>
  deliveryCompany?.find(
    (company) =>
      String(company?.deliveryOptionId) === String(picked?.deliveryOptionId) &&
      company?.integrationType === picked?.integrationType,
  );

const toMessage = (error) =>
  typeof error === "string" ? error : "Something went wrong please try again";

/**
 * The bulk-assign engine.
 *
 * Two passes, both `BULK_CONCURRENCY` orders at a time:
 *   1. `runCheck`  — GET each order's delivery-fee list, keep the matching
 *                    carrier object (that order's real price + fees + dates).
 *   2. `runAssign` — POST /shipment per eligible order with that exact object.
 *
 * The caller drives the two passes so it can put the wallet gate in between.
 *
 * Requests go through `dispatch(endpoints.X.initiate(...))` rather than the
 * generated hooks on purpose. A hook trigger stores its own pending/settled
 * state, so firing it 100 times re-renders this component 100 extra times — and
 * with three lanes in flight the hook's `data`/`isFetching` are meaningless
 * anyway. Only what each promise resolves to is trusted. Rejections arrive as
 * plain strings because every `mapitShipApi` endpoint sets
 * `transformErrorResponse`.
 */
export default function useBulkAssign() {
  const dispatch = useDispatch();

  // Per-order state lives in a ref so the lanes always see the latest values and
  // so a status change costs nothing until the next flush.
  const statesRef = useRef({});
  const [states, setStates] = useState({});

  const [phase, setPhase] = useState(BULK_PHASE.carrier);
  const [control, setControl] = useState(BULK_CONTROL.running);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const passDoneRef = useRef(0);
  const passTotalRef = useRef(0);
  const dirtyRef = useRef(false);
  const flushTimerRef = useRef(null);

  const controlRef = useRef(BULK_CONTROL.running);
  const busyRef = useRef(false);
  const resumeWaitersRef = useRef([]);

  const flush = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    dirtyRef.current = false;
    // New container identity so React commits; the row objects themselves are
    // only replaced when they actually change, so row memoization still holds.
    setStates({ ...statesRef.current });
    setProgress({ done: passDoneRef.current, total: passTotalRef.current });
  }, []);

  const scheduleFlush = useCallback(() => {
    dirtyRef.current = true;
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      if (dirtyRef.current) flush();
    }, FLUSH_INTERVAL_MS);
  }, [flush]);

  useEffect(
    () => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    },
    [],
  );

  const patch = useCallback(
    (orderId, changes) => {
      statesRef.current[orderId] = {
        ...statesRef.current[orderId],
        ...changes,
      };
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const bumpProgress = useCallback(() => {
    passDoneRef.current += 1;
    scheduleFlush();
  }, [scheduleFlush]);

  const startPass = useCallback(
    (total) => {
      passDoneRef.current = 0;
      passTotalRef.current = total;
      flush();
    },
    [flush],
  );

  const setControlFlag = useCallback((next) => {
    controlRef.current = next;
    setControl(next);
    if (next !== BULK_CONTROL.paused) {
      // Release anything parked in the pause gate.
      resumeWaitersRef.current.forEach((resolve) => resolve());
      resumeWaitersRef.current = [];
    }
  }, []);

  /** Awaited before each item: parks while paused, returns false once stopped. */
  const gate = useCallback(async () => {
    while (controlRef.current === BULK_CONTROL.paused) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        resumeWaitersRef.current.push(resolve);
      });
    }
    return controlRef.current !== BULK_CONTROL.stopped;
  }, []);

  const pause = useCallback(() => {
    if (controlRef.current === BULK_CONTROL.running) {
      setControlFlag(BULK_CONTROL.paused);
    }
  }, [setControlFlag]);

  const resume = useCallback(() => {
    if (controlRef.current === BULK_CONTROL.paused) {
      setControlFlag(BULK_CONTROL.running);
    }
  }, [setControlFlag]);

  const stop = useCallback(() => {
    setControlFlag(BULK_CONTROL.stopped);
  }, [setControlFlag]);

  /** Read the live control flag — `control` state can lag inside an async run. */
  const wasStopped = useCallback(
    () => controlRef.current === BULK_CONTROL.stopped,
    [],
  );

  /** Jump straight to the results screen without an assignment pass. */
  const finishRun = useCallback(() => setPhase(BULK_PHASE.done), []);

  /**
   * Seeds the round and clears any previous run. Orders that already have a
   * shipping company are recorded as such up front — the availability pass is
   * a price quote and would otherwise happily call them "eligible".
   */
  const prepare = useCallback(
    (orders) => {
      statesRef.current = (orders ?? []).reduce((acc, order) => {
        acc[order.id] = {
          orderId: order.id,
          orderNumber: order?.orderNumber,
          origin: order?.warehouse?.address?.city,
          destination: order?.user?.address?.city,
          weight:
            order?.otoIntegration?.calculatedWeight ??
            order?.otoIntegration?.actualWeight ??
            null,
          amountPayable: order?.amountPayable,
          currentStatus: order?.currentStatus ?? null,
          status: needsCarrier(order)
            ? BULK_STATUS.waiting
            : BULK_STATUS.alreadyAssigned,
          price: null,
          deliveryOption: null,
          errorMsg: null,
        };
        return acc;
      }, {});
      setControlFlag(BULK_CONTROL.running);
      flush();
    },
    [flush, setControlFlag],
  );

  /** Pass 1 — availability. Resolves with the eligible rows. */
  const runCheck = useCallback(
    async ({ orders, company, serviceType }) => {
      if (busyRef.current) return [];
      busyRef.current = true;
      setPhase(BULK_PHASE.checking);

      const candidates = (orders ?? []).filter(
        (order) =>
          statesRef.current[order.id]?.status === BULK_STATUS.waiting,
      );
      startPass(candidates.length);

      try {
        await runWithConcurrency(
          candidates,
          BULK_CONCURRENCY,
          async (order) => {
            patch(order.id, { status: BULK_STATUS.checking });

            const request = dispatch(
              mapitShipApi.endpoints.getDeliveryCompanies.initiate(
                {
                  orderId: order.id,
                  params: serviceType ? { serviceType } : {},
                },
                { forceRefetch: true },
              ),
            );

            try {
              const result = await request.unwrap();
              const match = findMatchingCompany(
                result?.deliveryCompany,
                company,
              );

              if (match) {
                patch(order.id, {
                  status: BULK_STATUS.eligible,
                  deliveryOption: match,
                  price: Number(match?.price),
                  errorMsg: null,
                });
              } else {
                patch(order.id, {
                  status: BULK_STATUS.unsupported,
                  deliveryOption: null,
                  price: null,
                });
              }
            } catch (error) {
              patch(order.id, {
                status: BULK_STATUS.failed,
                errorMsg: toMessage(error),
              });
            } finally {
              // Nothing is subscribed to this cache entry; let it expire.
              request.unsubscribe();
            }
            bumpProgress();
          },
          gate,
        );

        // Anything the user stopped before we reached it.
        Object.values(statesRef.current).forEach((row) => {
          if (
            row.status === BULK_STATUS.waiting ||
            row.status === BULK_STATUS.checking
          ) {
            patch(row.orderId, { status: BULK_STATUS.stopped });
          }
        });
        flush();

        // Read from the ref, not from `states`: React may not have committed the
        // last patches yet when this resolves.
        return Object.values(statesRef.current).filter(
          (row) => row.status === BULK_STATUS.eligible,
        );
      } finally {
        busyRef.current = false;
      }
    },
    [bumpProgress, dispatch, flush, gate, patch, startPass],
  );

  /**
   * Asks the server about every order this run has not settled and flips any
   * that no longer need a carrier. This is what makes the results screen
   * trustworthy after a stop: a request whose response was lost, or an
   * assignment made from another tab, would otherwise show up as "still to
   * do" when the order already has a shipping company.
   */
  const reconcileRemaining = useCallback(async () => {
    const unsettled = Object.values(statesRef.current).filter(
      (row) => !BULK_SETTLED_STATUSES.includes(row.status),
    );
    if (!unsettled.length) return;

    const request = dispatch(
      mapitShipApi.endpoints.getMapitShipOrdersByIds.initiate(
        { ids: unsettled.map((row) => row.orderId) },
        { forceRefetch: true },
      ),
    );

    try {
      const fresh = await request.unwrap();
      (fresh ?? []).forEach((order) => {
        if (statesRef.current[order?.id] && !needsCarrier(order)) {
          patch(order.id, {
            status: BULK_STATUS.alreadyAssigned,
            currentStatus: order.currentStatus,
            errorMsg: null,
          });
        }
      });
    } catch (error) {
      // Keep the client-side view; it is still an honest account of this run.
      console.error(error);
    } finally {
      request.unsubscribe();
      flush();
    }
  }, [dispatch, flush, patch]);

  /**
   * Pass 2 — assignment. Assigns every row currently in `fromStatus`
   * (`eligible` for a first run, `failed` for a retry) that has a resolved
   * `deliveryOption`.
   */
  const runAssign = useCallback(
    async (fromStatus = BULK_STATUS.eligible) => {
      if (busyRef.current) return undefined;
      busyRef.current = true;
      setPhase(BULK_PHASE.assigning);
      // Clear a previous stop (this is how "retry all" runs again) but keep a
      // pause: the user may have pressed it while the wallet was being checked.
      if (controlRef.current === BULK_CONTROL.stopped) {
        setControlFlag(BULK_CONTROL.running);
      }

      const targets = Object.values(statesRef.current).filter(
        (row) => row.status === fromStatus && !!row.deliveryOption,
      );
      startPass(targets.length);

      try {
        await runWithConcurrency(
          targets,
          BULK_CONCURRENCY,
          async (row) => {
            patch(row.orderId, {
              status: BULK_STATUS.assigning,
              errorMsg: null,
            });

            const request = dispatch(
              mapitShipApi.endpoints.assignDeliveryCompanySilent.initiate({
                orderId: row.orderId,
                deliveryOption: row.deliveryOption,
              }),
            );

            try {
              await request.unwrap();
              patch(row.orderId, { status: BULK_STATUS.assigned });
            } catch (error) {
              patch(row.orderId, {
                status: BULK_STATUS.failed,
                errorMsg: toMessage(error),
              });
            } finally {
              // Drop the tracked mutation entry so a 100-order run does not pile
              // 100 of them up in the store.
              request.reset?.();
            }
            bumpProgress();
          },
          gate,
        );

        // Rows we never reached because the user stopped: put them back to
        // eligible so "assign the remaining" still offers them.
        Object.values(statesRef.current).forEach((row) => {
          if (row.status === BULK_STATUS.assigning) {
            patch(row.orderId, { status: BULK_STATUS.eligible });
          }
        });
        flush();

        await reconcileRemaining();

        const assignedIds = Object.values(statesRef.current)
          .filter((row) => row.status === BULK_STATUS.assigned)
          .map((row) => row.orderId);

        // One invalidation for the whole batch — see the note on
        // `assignDeliveryCompanySilent` in mapitShipApi.js.
        if (assignedIds.length) {
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "MAPIT-SHIP-ORDER", id: "MAPIT-SHIP-ORDER-PARTIAL-LIST" },
              ...assignedIds.map((id) => ({ type: "MAPIT-SHIP-ORDER", id })),
            ]),
          );
        }

        const settledIds = Object.values(statesRef.current)
          .filter((row) => BULK_SETTLED_STATUSES.includes(row.status))
          .map((row) => row.orderId);

        return {
          assignedIds,
          settledIds,
          remainingIds: Object.values(statesRef.current)
            .filter((row) => !BULK_SETTLED_STATUSES.includes(row.status))
            .map((row) => row.orderId),
        };
      } finally {
        busyRef.current = false;
        setPhase(BULK_PHASE.done);
      }
    },
    [
      bumpProgress,
      dispatch,
      flush,
      gate,
      patch,
      reconcileRemaining,
      setControlFlag,
      startPass,
    ],
  );

  const retryFailed = useCallback(
    () => runAssign(BULK_STATUS.failed),
    [runAssign],
  );

  /** Back to step 1, keeping nothing from the previous round. */
  const reset = useCallback(() => {
    statesRef.current = {};
    passDoneRef.current = 0;
    passTotalRef.current = 0;
    setPhase(BULK_PHASE.carrier);
    setControlFlag(BULK_CONTROL.running);
    flush();
  }, [flush, setControlFlag]);

  const rows = useMemo(() => Object.values(states), [states]);

  const counts = useMemo(() => {
    const base = {
      total: rows.length,
      waiting: 0,
      checking: 0,
      eligible: 0,
      unsupported: 0,
      assigning: 0,
      assigned: 0,
      failed: 0,
      stopped: 0,
      alreadyAssigned: 0,
    };
    rows.forEach((row) => {
      if (base[row.status] !== undefined) base[row.status] += 1;
    });
    return base;
  }, [rows]);

  const eligibleCost = useMemo(
    () =>
      rows
        .filter((row) => row.status === BULK_STATUS.eligible)
        .reduce(
          (sum, row) => sum + (Number.isFinite(row.price) ? row.price : 0),
          0,
        ),
    [rows],
  );

  const assignedCost = useMemo(
    () =>
      rows
        .filter((row) => row.status === BULK_STATUS.assigned)
        .reduce(
          (sum, row) => sum + (Number.isFinite(row.price) ? row.price : 0),
          0,
        ),
    [rows],
  );

  return {
    rows,
    counts,
    phase,
    control,
    progress,
    eligibleCost,
    assignedCost,
    prepare,
    runCheck,
    runAssign,
    retryFailed,
    pause,
    resume,
    stop,
    wasStopped,
    finishRun,
    reconcileRemaining,
    reset,
  };
}
